import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AppBarWithButtons } from '../../../components/designe/app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppFormDateTimePicker from '../../../components/designe/forms/app-form-date-time-picker';
import AppFormTextField from '../../../components/designe/forms/app-form-text-field';
import AppDialog from '../../../components/reusable/app-dialog/AppDialog';
import TrainingActivityTypePicker from '../../../components/reusable/training-activity-type-picker';
import { useScrollToFirstError } from '../../../hooks/use-scroll-to-first-error';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { ITraining } from '../../../types/interfaces/training';
import { useStyles } from './EditTrainingModal.styles';
import { useEditTrainingModal } from './useEditTrainingModal';

interface INewExerciseModalProps {
  editedTraining: ITraining;
  finishDataEditable?: boolean;
  onCancel: () => void;
  isEditTraining: boolean;
}

export default function EditTrainingModal({
  editedTraining,
  isEditTraining,
  finishDataEditable = false,
  onCancel,
}: INewExerciseModalProps) {
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const { formik, isLoading, isSubmitDisabled } = useEditTrainingModal(
    editedTraining,
    onCancel,
    finishDataEditable,
  );
  const { allowAndLeave, runLeave, dialog } = useUnsavedChangesGuard({
    isDirty: formik.dirty && !formik.isSubmitting,
    leaveAction: onCancel,
  });

  useScrollToFirstError(formik, {
    fieldOrder: ['title', 'startDate', 'finishDate'],
  });

  return (
    <>
      {dialog}
      <AppDialog
        classes={{ root: classes.editTrainingDialog }}
        open={isEditTraining}
        onClose={() => runLeave(onCancel)}
        title={t('trainings.editTraining')}>
        <form
          className={classes.editTrainingForm}
          onSubmit={formik.handleSubmit}>
          <Box paddingTop={1} display="flex" flexDirection="column" gap={1}>
            <AppFormTextField
              label={t('common.title')}
              fullWidth
              autoFocus
              name="title"
              formik={formik}
              mode="dark"
            />
            <AppFormTextField
              label={t('common.description')}
              fullWidth
              multiline
              minRows={3}
              maxRows={15}
              name="description"
              formik={formik}
              mode="dark"
            />
            <TrainingActivityTypePicker formik={formik} fullWidth />
            <AppFormDateTimePicker
              label={t('trainings.startDate')}
              name="startDate"
              formik={formik}
              maxDateTime={new Date()}
              mode="dark"
            />
            {finishDataEditable && (
              <AppFormDateTimePicker
                label={t('trainings.finishDate')}
                name="finishDate"
                formik={formik}
                minDateTime={formik.values.startDate || null}
                maxDateTime={new Date()}
                mode="dark"
              />
            )}
            {finishDataEditable && (
              <AppFormTextField
                label={t('trainings.comment')}
                fullWidth
                multiline
                minRows={3}
                maxRows={15}
                name="comment"
                formik={formik}
                mode="dark"
              />
            )}
          </Box>
          <Box className={classes.buttonWrapper}>
            <AppBarWithButtons
              onCancelClick={allowAndLeave}
              loading={isLoading}
              disabled={isSubmitDisabled}
            />
          </Box>
        </form>
      </AppDialog>
    </>
  );
}
